/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { type ReactNode, useState } from 'react';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';

/* Opening a group used to drop its whole task list in within one frame, which
   read as a glitch rather than a reveal — everything below jumped by the height
   of the list at once. Closing had the same problem in reverse: everything
   below snapped up the instant a group collapsed, with no sense of where the
   list had gone. The list only exists in the DOM while it is open or animating
   shut (`KaiNavTaskList` owns that lifecycle itself, off its own `expanded`
   prop, rather than being mounted and unmounted by its host), so both the
   reveal and its reverse are mount/exit animations, and animating
   `grid-template-rows` from/to `0fr` carries the height from or to nothing
   without anyone having to measure it. Browsers that can't interpolate it hold
   at `1fr`/`0fr` and cut over instantly, as before.

   80ms is deliberately short: long enough that the rows are seen arriving or
   leaving, not long enough to make opening or closing a group feel like
   waiting for it. */
const REVEAL_DURATION_MS = 80;

const TASK_LIST_REVEAL_STYLE = css`
  label: KAI_NAV_TASK_LIST_REVEAL_STYLE;
  display: grid;
  grid-template-rows: 1fr;
  animation: kaiNavTaskListReveal ${REVEAL_DURATION_MS}ms ease-out;

  @keyframes kaiNavTaskListReveal {
    from { grid-template-rows: 0fr; }
    to { grid-template-rows: 1fr; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** The reveal run backwards — same duration and curve, so a group closing
 *  reads as the same gesture undone rather than a different one. */
const TASK_LIST_COLLAPSE_STYLE = css`
  label: KAI_NAV_TASK_LIST_COLLAPSE_STYLE;
  display: grid;
  grid-template-rows: 0fr;
  animation: kaiNavTaskListCollapse ${REVEAL_DURATION_MS}ms ease-out;

  @keyframes kaiNavTaskListCollapse {
    from { grid-template-rows: 1fr; }
    to { grid-template-rows: 0fr; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    grid-template-rows: 0fr;
  }
`;

/** `min-height` lets the row above squeeze this to nothing during either
 *  transition. */
const TASK_LIST_STYLE = css`
  label: KAI_NAV_TASK_LIST_STYLE;
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
  min-height: 0;
`;

/** Open: clipped only for the length of the reveal, then let out — held past
 *  it, `overflow` would keep clipping this box forever, including a task
 *  row's own kebab dropdown the instant it drops below the row it belongs to. */
const TASK_LIST_OPEN_OVERFLOW_STYLE = css`
  overflow: hidden;
  animation: kaiNavTaskListRevealOverflow ${REVEAL_DURATION_MS}ms forwards;

  /* One step, right at the end — not a 50/50 split of the reveal — since
     overflow is a discrete property with only one real keyframe to jump to. */
  @keyframes kaiNavTaskListRevealOverflow {
    0%, 99% { overflow: hidden; }
    100% { overflow: visible; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    overflow: visible;
  }
`;

/** Closing: clipped for the whole transition, no reveal step — content on its
 *  way out has no business escaping the box it is shrinking inside of. */
const TASK_LIST_CLOSING_OVERFLOW_STYLE = css`
  overflow: hidden;
`;

/** Whether the browser will actually animate `grid-template-rows` — reduced
 *  motion turns the animation off via CSS, but the closing phase also needs to
 *  know this in JS: with no animation to run, `onAnimationEnd` never fires,
 *  so waiting for it would leave the list stuck open forever. Read once per
 *  close rather than watched live — a change mid-session is vanishingly rare,
 *  and the CSS rules above already cover it visually either way. */
const prefersReducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

type ListPhase = 'open' | 'closing';

/**
 * The task rows of one open group or contractor, revealed as it opens and
 * eased away as it closes.
 *
 * Owns its own mount lifecycle off `expanded` rather than being conditionally
 * rendered by its host: closing needs one more frame in the DOM to animate
 * out before it actually unmounts, which only this component can arrange for
 * itself. A collapsed group that was never opened stays fully unmounted,
 * exactly as before — nothing here pays for a close animation it never runs.
 *
 * Opening reacts to `expanded` synchronously during render (comparing it
 * against a mirrored `trackedExpanded` and adjusting `phase` right there,
 * rather than in an effect — see React's own "you might not need an effect"
 * guidance for this exact pattern). An effect would put the rows one render
 * behind `expanded` itself, which is exactly the extra pass a host driving
 * its own scroll-into-view off `expanded` can't spare — it would go looking
 * for a row that hasn't entered the DOM yet. Closing has no such deadline, so
 * only its own exit — actually leaving the DOM once the animation ends —
 * stays effect/event-driven, off `onAnimationEnd`.
 */
export const KaiNavTaskList = ({ expanded, children }: { expanded: boolean; children: ReactNode }) => {
  const [phase, setPhase] = useState<ListPhase | undefined>(expanded ? 'open' : undefined);
  const [trackedExpanded, setTrackedExpanded] = useState(expanded);

  if (expanded !== trackedExpanded) {
    setTrackedExpanded(expanded);
    if (expanded) {
      setPhase('open');
    } else {
      setPhase(previous => {
        // Never opened (or already closed) — nothing to animate away.
        if (previous === undefined) return undefined;
        return prefersReducedMotion() ? undefined : 'closing';
      });
    }
  }

  if (phase === undefined) return null;

  return (
    <div
      css={phase === 'open' ? TASK_LIST_REVEAL_STYLE : TASK_LIST_COLLAPSE_STYLE}
      onAnimationEnd={phase === 'closing' ? () => setPhase(undefined) : undefined}
      data-testid='kai-nav-task-list'
    >
      <div css={[TASK_LIST_STYLE, phase === 'open' ? TASK_LIST_OPEN_OVERFLOW_STYLE : TASK_LIST_CLOSING_OVERFLOW_STYLE]}>
        {children}
      </div>
    </div>
  );
};
